/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import StringResources from 'nls/StringResources';
import delete32 from '@ba/ba-graphics/dist/icons-js/delete_32';

/**
 * Models the delete card action
 */
class BaseDeleteAction {
	constructor(options) {
		this.exploreVM = options.exploreVM;
		this.layoutModel = options.layoutModel;
	}

	getAttributes() {}

	isEnabled(cards) {
		return !!cards.length;
	}

	/*
	 * We always want to select the card closest to the end - so this function
	 * finds either the last card to be deleted (getCardToBeDeleted=true) or
	 * the last card not to be deleted (getCardToBeDeleted=false).
	 */
	_findIndexOfLastCard(cardsToBeDeleted, getCardToBeDeleted) {
		for (let i = this.layoutModel.items.length - 1; i >= 0; i--) {
			const card = this.layoutModel.items[i];
			const cardIsToBeDeleted = cardsToBeDeleted.indexOf(card) >= 0;
			if(cardIsToBeDeleted === getCardToBeDeleted) {
				return i;
			}
		}
		return -1;
	}

	/*
	 * Decides which card to display which is NOT being deleted - and returns its
	 * pre-deletion index (so we can change the displayed card to it BEFORE
	 * performing the delete) - return -1 if there are no cards left
	 */
	_getNewDisplayedCardIndex(cardsToBeDeleted) {
		const lastCardToBeDeletedIndex = this._findIndexOfLastCard(cardsToBeDeleted, true);
		if (lastCardToBeDeletedIndex < this.layoutModel.items.length - 1) {
			//Display the card AFTER the last card to be deleted
			return lastCardToBeDeletedIndex + 1;
		} else {
			//There is no card after the last card to be deleted, so display the last
			//card not to be deleted
			return this._findIndexOfLastCard(cardsToBeDeleted, false);
		}
	}

	_getNewDisplayedCard(cardsToBeDeleted) {
		const index = this._getNewDisplayedCardIndex(cardsToBeDeleted);
		if (index >= 0) {
			return this.layoutModel.items[index];
		} else {
			return null;
		}
	}

	_updateDisplayedCard(cardsToBeDeleted) {
		const displayCard = this._getNewDisplayedCard(cardsToBeDeleted);
		if (displayCard) {
			this.exploreVM.setDisplayedToCard(displayCard.id);
		} else {
			this.exploreVM.setDisplayedToStartingPoints();
		}
	}

	/**
	 * For single cards, this action will always delete the entire card
	 * For compare cards:
	 * 	- If 2 LW are selected, then both will be replaced with placeholders
	 * 	- If 1 LW is selected, then the LW will be replaced with placeholder
	 * 	- If 1 LW and 1 Placeholder is selected, the LW will be replaced with a placeholder
	 * 	- If 1 Placeholder is selected, the card will be deleted
	 * 	- If 2 placeholders are selected, the card will be deleted
	 * 	- If no widgets are selected, the card will be deleted
	 */
	action() {}

	deleteCards(cards) {
		this._updateDisplayedCard(cards);
		const cardIds = cards.map(card => card.id);
		this.layoutModel.deleteCards(cardIds);
	}

	deleteWidgets() {
		const selectedWidgets = this.exploreVM.selectedWidgets;
		const visIds = selectedWidgets.map(widget => widget.getId());
		this.layoutModel.deleteVis(visIds);
	}
}

class DeleteCardAction extends BaseDeleteAction {
	constructor(options) {
		super(options);
	}

	getAttributes() {
		return {
			id: 'deleteCard',
			icon: delete32.id,
			label: StringResources.get('card_sidebar_delete'),
			className: null
		};
	}

	action(cards) {
		this.deleteCards(cards);
	}
}

class DeleteWidgetAction extends BaseDeleteAction {
	constructor(options) {
		super(options);
	}

	getAttributes() {
		return {
			id: 'deleteWidget',
			icon: delete32.id,
			label: StringResources.get('card_sidebar_delete'),
			className: null
		};
	}

	action(cards) {
		// Even though we have descrete action handlers for deletion of cards and widgets,
		// we have a special case: when pressing toolbar's delete, it should delete selected widget first,
		// and if none of the widgets are selected OR no widgets left on the card, we need to delete the card.
		// ideally we'd want to call respective action handler from the Toolbar action, but since we process
		// action handlers generically, we have to branch delete logic for both cases in this handler
		const selectedWidgets = this.exploreVM.selectedWidgets;
		const containsLiveWidget = selectedWidgets.find(widget => widget.getType() !== 'placeholder');
		if (this.exploreVM.isCompare() && containsLiveWidget) {
			this.deleteWidgets();
		} else {
			this.deleteCards(cards);
		}
	}
}

export { DeleteWidgetAction, DeleteCardAction };



// WEBPACK FOOTER //
// ./src/js/components/cards/actions/DeleteActions.js